import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { Spin } from 'antd';
import { DatePicker, Dropdown } from '@maxtropy/components';
import SwitchDate, {
  IndexTimeActive,
} from '@/pages/WgqAllMaps/components/UnityEnergyCockpitMap/components/LeftBox/components/SwitchDate';
import {
  apiV2AirCompressorOverviewCountPost,
  apiV2AirCompressorOverviewEfficiencyRankingPost,
  V2AirCompressorOverviewCountPostResponse,
  V2AirCompressorOverviewEfficiencyRankingPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import titleIcon from '@/assets/images/title_icon.png';
import runningIcon from '../../imgs/load.png';
import standbyIcon from '../../imgs/unload.png';
import stoppedIcon from '../../imgs/stop.png';
import styles from './index.module.scss';
import { formatNumber, OVERVIEW_POLLING_INTERVAL } from '../../utils';

const { RangePicker } = DatePicker;

type RankMode = 'day' | 'month';
type CountKey = keyof Pick<V2AirCompressorOverviewCountPostResponse, 'runCount' | 'waitCount' | 'stopCount'>;
type RankItem = NonNullable<V2AirCompressorOverviewEfficiencyRankingPostResponse['rankings']>[number];

const statusList: {
  key: CountKey;
  label: string;
  icon: string;
  className: string;
}[] = [
  { key: 'runCount', label: '运行(台)', icon: runningIcon, className: 'running' },
  { key: 'waitCount', label: '待机(台)', icon: standbyIcon, className: 'standby' },
  { key: 'stopCount', label: '停机(台)', icon: stoppedIcon, className: 'stopped' },
];

const deviceTypeLabelMap: Record<number, string> = {
  1: '新',
  2: '旧',
};

const emptyRankList: RankItem[] = [];
const RANK_VISIBLE_COUNT = 3;
const RANK_ROW_HEIGHT = 40;
const RANK_SCROLL_INTERVAL = 5000;

const formatRatio = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '--';
  }
  return value.toFixed(4);
};

const getDeviceSpecText = (item: RankItem): React.ReactNode => {
  if (item.type === undefined && item.spec === undefined) {
    return '--';
  }
  const typeText = item.type !== undefined ? deviceTypeLabelMap[item.type] || '--' : '--';
  const specText = item.spec ?? '--';
  const isNew = item.type === 1;
  return (
    <span className={`${styles.deviceTypeBadge} ${isNew ? styles.deviceTypeBadgeNew : styles.deviceTypeBadgeOld}`}>
      {typeText}({specText})
    </span>
  );
};

const RightOverview: React.FC = () => {
  const [rankMode, setRankMode] = useState<RankMode>('month');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(1, 'month').startOf('day'),
    dayjs().endOf('day'),
  ]);
  const [activeKey, setActiveKey] = useState<IndexTimeActive>(IndexTimeActive.THIS_MONTH);
  const [value, setValue] = useState<Dayjs>(dayjs());
  const [rankScrollIndex, setRankScrollIndex] = useState(0);
  const [rankScrollTransition, setRankScrollTransition] = useState(true);
  const [isSmallScreen, setIsSmallScreen] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth <= 2560
  );

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth <= 2560);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { data: countData, loading: countLoading } = useRequest(() => apiV2AirCompressorOverviewCountPost({}), {
    pollingInterval: OVERVIEW_POLLING_INTERVAL,
  });
  const rankingQuery = useMemo(() => {
    if (isSmallScreen) {
      const isMonth = activeKey === IndexTimeActive.THIS_MONTH;
      return {
        timeType: isMonth ? 1 : 2,
        startTime: value.startOf(isMonth ? 'month' : 'year').valueOf(),
        endTime: value.endOf(isMonth ? 'month' : 'year').valueOf(),
      };
    }

    return {
      timeType: rankMode === 'day' ? 1 : 2,
      startTime: dateRange[0].startOf('day').valueOf(),
      endTime: dateRange[1].endOf('day').valueOf(),
    };
  }, [activeKey, dateRange, isSmallScreen, rankMode, value]);
  const { data: rankingData, loading: rankingLoading } = useRequest(
    () => apiV2AirCompressorOverviewEfficiencyRankingPost(rankingQuery),
    {
      pollingInterval: OVERVIEW_POLLING_INTERVAL,
      refreshDeps: [rankingQuery],
    }
  );

  const rankList = useMemo(() => rankingData?.rankings ?? emptyRankList, [rankingData?.rankings]);
  const shouldScrollRank = rankList.length > RANK_VISIBLE_COUNT;
  const rankRenderList = useMemo(
    () => (shouldScrollRank ? [...rankList, ...rankList.slice(0, RANK_VISIBLE_COUNT)] : rankList),
    [rankList, shouldScrollRank]
  );
  const loading = (countLoading && !countData) || (rankingLoading && !rankingData);
  const ratioUnit = 'kWh/m³';

  useEffect(() => {
    setRankScrollTransition(false);
    setRankScrollIndex(0);
    const frameId = window.requestAnimationFrame(() => {
      setRankScrollTransition(true);
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [rankList]);

  useEffect(() => {
    if (!shouldScrollRank) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setRankScrollTransition(true);
      setRankScrollIndex(index => index + 1);
    }, RANK_SCROLL_INTERVAL);

    return () => window.clearInterval(timer);
  }, [shouldScrollRank]);

  const handleRankTransitionEnd = () => {
    if (!shouldScrollRank || rankScrollIndex < rankList.length) {
      return;
    }

    setRankScrollTransition(false);
    setRankScrollIndex(0);
    window.requestAnimationFrame(() => {
      setRankScrollTransition(true);
    });
  };

  return (
    <Spin spinning={loading} wrapperClassName={styles.overviewLoading}>
      <section className={styles.overview}>
        <div className={styles.statusCard}>
          <div className={styles.totalTitle}>
            <span>空压机总计(台):</span>
            <strong>{countData?.totalCount ?? 0}</strong>
          </div>
          <div className={styles.statusGrid}>
            {statusList.map(item => (
              <div key={item.key} className={styles.statusItem}>
                <img src={item.icon} alt="" className={styles.statusIcon} />
                <span className={classNames(styles.statusLabel, styles[item.className])}>{item.label}</span>
                <span className={styles.statusValue}>{formatNumber(countData?.[item.key], 0)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.rankCard}>
          <div className={styles.rankHeader}>
            <div className={styles.rankTitle}>
              <img src={titleIcon} alt="" />
              <span>空压机能效排名</span>
            </div>

            {isSmallScreen ? (
              <Dropdown
                type="link"
                className={styles.dropdown_wrapper}
                getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
                trigger={['click']}
                dropdownRender={() => (
                  <div className={styles.dropdown_menu_wrapper}>
                    <SwitchDate activeKey={activeKey} value={value} setActiveKey={setActiveKey} setValue={setValue} />
                  </div>
                )}
              >
                {dayjs(value).format(activeKey === IndexTimeActive.THIS_MONTH ? 'YYYY-MM' : 'YYYY')}
              </Dropdown>
            ) : (
              <div className={classNames(styles.rankTools, styles.largeRankTools)}>
                <button
                  type="button"
                  className={classNames(styles.modeButton, rankMode === 'day' && styles.modeButtonActive)}
                  onClick={() => setRankMode('day')}
                >
                  按日
                </button>
                <button
                  type="button"
                  className={classNames(styles.modeButton, rankMode === 'month' && styles.modeButtonActive)}
                  onClick={() => setRankMode('month')}
                >
                  按月
                </button>
                <RangePicker
                  value={dateRange}
                  allowClear={false}
                  format="YYYY-MM-DD"
                  className={styles.rangePicker}
                  getPopupContainer={() => document.querySelector('.compressorOverviewPage') || document.body}
                  onChange={range => {
                    if (range?.[0] && range?.[1]) {
                      setDateRange([range[0], range[1]]);
                    }
                  }}
                />
              </div>
            )}
          </div>

          <div className={styles.rankList}>
            {rankList.length ? (
              <div
                className={classNames(styles.rankScroller, !rankScrollTransition && styles.rankScrollerNoTransition)}
                style={{ transform: `translateY(-${rankScrollIndex * RANK_ROW_HEIGHT}px)` }}
                onTransitionEnd={handleRankTransitionEnd}
              >
                {rankRenderList.map((item, index) => {
                  const realIndex = index % rankList.length;
                  const rank = realIndex + 1;
                  const rankIndexClass = rank <= 3 ? styles[`rankIndex${rank}`] : styles.rankIndexOther;
                  return (
                    <div key={`${item.compressorName || 'rank'}-${rank}-${index}`} className={styles.rankItem}>
                      <span className={classNames(styles.rankIndex, rankIndexClass)}>{rank}</span>
                      <span className={styles.rankName} title={item.compressorName}>
                        {item.compressorName || '--'}
                      </span>
                      <span className={styles.rankSpec}>
                        <span className={styles.rankSpecLabel}>类型规格:</span>
                        <b>{getDeviceSpecText(item)}</b>
                      </span>
                      <span className={styles.rankRatio}>
                        <span className={styles.rankRatioLabel}>气电比:</span>
                        <span className={styles.rankRatioValue}>
                          <strong>{formatRatio(item.ratio)}</strong>
                          <em>{ratioUnit}</em>
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.emptyRank}>暂无排行数据</div>
            )}
          </div>
        </div>
      </section>
    </Spin>
  );
};

export default RightOverview;
